import './index.scss'

import { Field, Switch } from '@chakra-ui/react'
import { mdiAlertOutline, mdiBookOpenVariantOutline } from '@mdi/js'
import Icon from '@mdi/react'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { Flex } from '@/components/ui/flex'
import { Link } from '@/components/ui/link'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { Stack } from '@/components/ui/stack'
import { Tooltip } from '@/components/ui/tooltip'
import { Text } from '@/components/ui/typography'
import { currentEnv } from '@/env/vars'
import useColorModeValue from '@/hooks/useColorModeValue'
import { openPromptLibraryDrawer } from '@/store/gigiChat/ChatAtom.ts'
import { IConversationSettings, IGroupedData, ISnippetSourceBundle, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'

import TemperatureSlider from '../components/slider'

const ChatSettings = ({
    chatSettings,
    options,
    onSettingsChange,
    defaultBundle,
}: {
    chatSettings: IConversationSettings | null
    options: ISnippetSourceBundle[]
    defaultBundle: string
    onSettingsChange: (processingStrategy: TProcessingStrategy, key: string, checkValue: string | number) => void
}) => {
    const { t } = useTranslation()
    const textColor = useColorModeValue('rgba(84, 79, 64, 0.7)', 'var(--chakra-colors-gray-500)')
    const [isWebSearch, setIsWebSearch] = useState<boolean>(chatSettings?.processingStrategyWriting?.model === 'bing-grounding')

    useEffect(() => {
        if (chatSettings?.processingStrategy === 'Writing') {
            setIsWebSearch(chatSettings?.processingStrategyWriting?.model === 'bing-grounding')
        }
    }, [chatSettings])

    const handleSettingsChange = (processingStrategy: TProcessingStrategy, key: string, checkValue: string | number) => {
        onSettingsChange(processingStrategy, key, checkValue)
    }
    const isDisabled = chatSettings?.processingStrategyWriting.mode === 'promptLibrary'

    const displayLabelData = (options: ISnippetSourceBundle[]) => {
        return options.map((option) => (
            <Radio
                value={option.bundleName}
                key={option.bundleName}
                onChange={() => handleSettingsChange('QA', option.bundleName, option.bundleName)}
                mb="12px"
            >
                <Text fontSize="14px" letterSpacing="0.25px">
                    {option.bundleName}
                </Text>
            </Radio>
        ))
    }

    const generateQaSettings = () => {
        const sortedGroupedData: IGroupedData = options
            .sort((a: { label: string; orderNumber: number }, b: { label: string; orderNumber: number }) => {
                return a.orderNumber - b.orderNumber
            })
            .reduce((acc: IGroupedData, item) => {
                const label = item.label
                if (!acc[label]) {
                    acc[label] = []
                }
                acc[label].push(item)
                return acc
            }, {})

        const labelsData = Object.keys(sortedGroupedData)

        return (
            <RadioGroup value={defaultBundle} variant="gigiOutline">
                {labelsData.map((label, index) => (
                    <React.Fragment key={label + index}>
                        {labelsData.length > 1 && (
                            <Box key={label} mb="4px">
                                <Text color={textColor} fontSize="12px" letterSpacing="0.5px">
                                    {label}
                                </Text>
                            </Box>
                        )}
                        <Box display="grid" mb="4px">
                            {displayLabelData(sortedGroupedData[label])}
                        </Box>
                    </React.Fragment>
                ))}
            </RadioGroup>
        )
    }

    const disabledColor = useColorModeValue('#6A6A6A', 'rgba(255, 255, 255, 0.4)')
    return (
        <Box position="relative" data-testid="settings-radio-options" className={isDisabled ? 'settings-disabled' : ''}>
            <Box data-testid="settings-radio-qa">
                <Flex>
                    <RadioGroup value={chatSettings?.processingStrategy} variant="gigiOutline">
                        <Radio
                            value="QA"
                            onChange={() => handleSettingsChange('QA', defaultBundle, defaultBundle)}
                            mb="12px"
                            fontSize="14px"
                        >
                            <Text fontSize="14px" letterSpacing="0.25px" display="inline">
                                {t('chat.settings.chips.searchGSKKnowledge')}
                            </Text>
                        </Radio>
                    </RadioGroup>
                    <Tooltip
                        portalled
                        variant="settings"
                        content={t('chat.settings.chips.searchGSKKnowledgeTooltip')}
                        css={{
                            marginTop: '3px',
                            marginLeft: '8px',
                        }}
                    />
                </Flex>
                {chatSettings?.processingStrategy === 'QA' && (
                    <Stack pl={6} mt={1} gap={1}>
                        {generateQaSettings()}
                    </Stack>
                )}
            </Box>
            <Flex pb="24px" data-testid="settings-radio-writing">
                <RadioGroup value={chatSettings?.processingStrategy} variant="gigiOutline">
                    <Radio
                        value="Writing"
                        onChange={() => {
                            handleSettingsChange('Writing', 'model', 'gpt-4')
                            setIsWebSearch(false)
                        }}
                        my="8px"
                        mt="0px"
                    >
                        <Text fontSize="14px" letterSpacing="0.25px">
                            {t('chat.settings.chips.helpWithWriting')}
                        </Text>
                    </Radio>
                </RadioGroup>
                <Tooltip
                    portalled
                    content={t('chat.settings.chips.helpWithWritingTooltip')}
                    variant="settings"
                    css={{
                        marginTop: '3px',
                        marginLeft: '8px',
                    }}
                />
            </Flex>
            {chatSettings?.processingStrategy === 'Writing' && (
                <>
                    <TemperatureSlider
                        onChange={(value: number) => handleSettingsChange('Writing', 'temperature', value)}
                        value={chatSettings?.processingStrategyWriting.temperature || 0}
                        hasOpacity={isWebSearch || isDisabled}
                    />
                    {currentEnv.features.isEnabled('webSearch') && (
                        <Field.Root
                            display="flex"
                            flexDirection="row"
                            mt="18px"
                            alignItems="center"
                            fontSize="14px"
                            data-testid="chat-settings-web-search"
                        >
                            <Switch.Root
                                id="isRequired"
                                size="md"
                                variant="gigi"
                                onChange={() => {
                                    handleSettingsChange('Writing', 'model', !isWebSearch ? 'bing-grounding' : 'gpt-4')
                                    setIsWebSearch(!isWebSearch)
                                }}
                                checked={isWebSearch}
                                ml="24px"
                                fontSize="14px"
                            >
                                <Switch.HiddenInput />
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Trans
                                    i18nKey={'chat.settings.writing.webSearch.label'}
                                    components={{
                                        span: (
                                            <span
                                                style={{
                                                    color: 'var(--chakra-colors-secondary-850)',
                                                    fontSize: '12px',
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            </Switch.Root>
                            {isWebSearch && (
                                <Box display="flex" alignItems="center">
                                    <Icon path={mdiAlertOutline} size="18px" color="var(--chakra-colors-status-warning)" />
                                    <Box fontSize="12px">
                                        <Trans
                                            i18nKey={'chat.settings.writing.webSearch.tou'}
                                            components={{
                                                link1: (
                                                    <Link
                                                        ml="4px"
                                                        href="https://gsk-qualitysuite.veevavault.com/ui/#permalink=V0Z000000050030"
                                                        isExternal
                                                        key="https://gsk-qualitysuite.veevavault.com/ui/#permalink=V0Z000000050030"
                                                        textDecoration="underline"
                                                    />
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Field.Root>
                    )}
                    {currentEnv.features.isEnabled('promptLibrary') && (
                        <Flex alignItems="center" marginTop="24px" marginLeft="18px" data-testid="chat-settings-prompt-library">
                            <Button
                                data-id="prompt-library-settings"
                                // leftIcon={<Icon path={mdiBookOpenVariantOutline} size="20px" />}
                                icon={<Icon path={mdiBookOpenVariantOutline} size="20px" />}
                                pl="12px"
                                pr="16px"
                                py="8px"
                                variant="secondary"
                                fontSize="16px"
                                fontWeight="400"
                                onClick={openPromptLibraryDrawer}
                                disabled={isDisabled}
                                data-testid="chat-settings-prompt-library-button"
                            >
                                {t('chat.settings.promptLibrary.label')}
                                <Text fontSize="12px" pl="8px" color={disabledColor}>
                                    ({t('chat.promptLib.drawer.betaLabel')})
                                </Text>
                            </Button>
                            <Tooltip
                                portalled
                                content={t('chat.settings.promptLibrary.tooltip')}
                                variant="settings"
                                css={{
                                    marginTop: '4px',
                                    marginLeft: '8px',
                                }}
                            />
                        </Flex>
                    )}
                </>
            )}
        </Box>
    )
}

export default ChatSettings
